import { useState } from "react";
import { X, Mail, Github, KeyRound, ArrowLeft } from "lucide-react";
import type { UseAuth } from "@/hooks/useAuth";
import { t } from "@/lib/i18n";

/**
 * Auth modal (spec 06 §2 screen 4).
 *
 * Uses email OTP (6-digit code) flow:
 *   Step 1: Enter email → code is sent
 *   Step 2: Enter 6-digit code → verified, session created
 *
 * Also offers OAuth and passkey (beta) alternatives.
 */
export interface AuthModalProps {
  auth: UseAuth;
  onClose: () => void;
}

export function AuthModal({ auth, onClose }: AuthModalProps) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await auth.sendOtp(email);
      setSent(true);
      setStep("code");
    } catch (e) {
      setError(e instanceof Error ? e.message : t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await auth.verifyOtp(email, code);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  const oauth = async (provider: "github" | "google" | "apple") => {
    setError(null);
    try {
      await auth.signInWithOAuth(provider);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("errors.generic"));
    }
  };

  const passkey = async () => {
    setError(null);
    try {
      await auth.signInWithPasskey();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("auth.passkeyUnsupported"));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center fade-in sm:items-center">
      <button
        type="button"
        aria-label={t("auth.cancel")}
        onClick={onClose}
        className="fixed inset-0 bg-black/60"
      />
      <div
        className="relative w-full max-w-md rounded-t-[var(--radius-sheet)] bg-bg-elevated p-5 pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] shadow-2xl sm:rounded-2xl sheet-up"
        role="dialog"
        aria-modal="true"
      >
        <div className="mx-auto mb-3 sheet-grabber sm:hidden" />
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {step === "code" && (
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setSent(false);
                  setCode("");
                  setError(null);
                }}
                className="rounded-full p-3 text-fg-muted hover:bg-surface"
                aria-label={t("auth.back")}
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-fg">{t("auth.title")}</h2>
              <p className="mt-1 text-sm text-fg-muted">{t("auth.subtitle")}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-3 text-fg-muted hover:bg-surface"
            aria-label={t("auth.cancel")}
          >
            <X size={20} />
          </button>
        </div>

        <div className="mt-5 space-y-3">
          {step === "email" ? (
            <>
              {/* Email input */}
              <div>
                <label className="mb-1 block text-xs font-medium text-fg-muted">
                  {t("auth.emailLabel")}
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && email.includes("@")) sendCode();
                      }}
                      placeholder={t("auth.emailPlaceholder")}
                      className="w-full rounded-lg bg-bg py-2.5 pl-9 pr-3 text-sm text-fg outline-none ring-1 ring-line focus:ring-accent"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={busy || !email.includes("@")}
                    onClick={sendCode}
                    className="rounded-lg bg-accent px-4 text-sm font-semibold text-accent-contrast disabled:opacity-40"
                  >
                    {t("auth.sendCode")}
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 py-1">
                <div className="h-px flex-1 bg-line" />
              </div>

              {/* Passkey */}
              <button
                type="button"
                onClick={passkey}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-surface py-2.5 text-sm font-medium text-fg ring-1 ring-line"
              >
                <KeyRound size={16} />
                {t("auth.passkey")}
              </button>

              {/* OAuth */}
              <button
                type="button"
                onClick={() => oauth("github")}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-surface py-2.5 text-sm font-medium text-fg ring-1 ring-line"
              >
                <Github size={16} />
                {t("auth.oauthGithub")}
              </button>
            </>
          ) : (
            <>
              {/* Code verification — works for both OTP code and magic link */}
              {sent && (
                <div className="rounded-xl bg-accent/10 p-3 space-y-1">
                  <p className="text-sm text-accent">
                    {t("auth.codeSent")}
                  </p>
                  <p className="text-xs text-fg-muted">
                    {t("auth.magicLinkHint")}
                  </p>
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs font-medium text-fg-muted">
                  {t("auth.codeLabel")}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && code.length === 6) verifyCode();
                  }}
                  placeholder="123456"
                  className="w-full rounded-lg bg-bg py-3 px-3 text-center text-2xl tracking-[0.5em] text-fg outline-none ring-1 ring-line focus:ring-accent"
                  autoFocus
                />
              </div>
              <button
                type="button"
                disabled={busy || code.length !== 6}
                onClick={verifyCode}
                className="w-full rounded-lg bg-accent py-2.5 text-sm font-semibold text-accent-contrast disabled:opacity-40"
              >
                {busy ? t("auth.verifying") : t("auth.verify")}
              </button>
            </>
          )}
        </div>

        {error ? (
          <p className="mt-3 text-sm text-legal-red">{error}</p>
        ) : null}
      </div>
    </div>
  );
}
